(() => {
  const WIDGET_SELECTOR = '[data-kc-floating-chat]';
  const STORAGE_PREFIX = 'agent_askme_chat';
  const DISMISSED_KEY = `${STORAGE_PREFIX}_dismissed_session`;

  const safeUUID = () => {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    return `aa_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  };

  const getStoredId = (key) => {
    try {
      const existing = window.localStorage.getItem(key) || window.sessionStorage.getItem(key);
      if (existing) return existing;
    } catch (error) {
      return safeUUID();
    }
    return safeUUID();
  };

  const setStoredId = (key, value, persistent = false) => {
    try {
      const storage = persistent ? window.localStorage : window.sessionStorage;
      storage.setItem(key, value);
    } catch (error) {
      // Storage can be blocked by browser privacy controls. The chat can still run for the current page view.
    }
  };

  const isSmallScreen = () => {
    try {
      return window.matchMedia('(max-width: 749px)').matches;
    } catch (error) {
      return window.innerWidth <= 749;
    }
  };

  const getSessionFlag = (key) => {
    try {
      return window.sessionStorage.getItem(key) === 'true';
    } catch (error) {
      return false;
    }
  };

  const setSessionFlag = (key, value) => {
    try {
      if (value) window.sessionStorage.setItem(key, 'true');
      else window.sessionStorage.removeItem(key);
    } catch (error) {
      // Storage can be blocked; dismissal will still work for the current DOM state.
    }
  };

  const escapeHTML = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const linkify = (text) => {
    const escaped = escapeHTML(text);
    return escaped.replace(/(https?:\/\/[^\s<]+)/g, (match) => {
      const trailing = match.match(/[;:,.!?)]*$/)?.[0] || '';
      const url = trailing ? match.slice(0, -trailing.length) : match;
      return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>${trailing}`;
    });
  };

  const getConsentState = () => {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.currentVisitorConsent === 'function') {
        return JSON.stringify(window.Shopify.customerPrivacy.currentVisitorConsent());
      }
    } catch (error) {
      return 'unknown';
    }
    return 'unknown';
  };

  const getCustomerContext = (root) => {
    const loggedIn = root.dataset.customerLoggedIn === 'true' || root.dataset.customerAuthenticated === 'true';
    const firstName = root.dataset.customerFirstName || '';
    const lastName = root.dataset.customerLastName || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return {
      authenticated: loggedIn,
      id: root.dataset.customerId || '',
      shopify_customer_id: root.dataset.customerId || root.dataset.shopifyCustomerId || '',
      name: fullName || firstName || root.dataset.customerName || '',
      email: root.dataset.customerEmail || '',
      phone: root.dataset.customerPhone || ''
    };
  };

  const buildPayload = (root, message) => {
    let visitorId = getStoredId(`${STORAGE_PREFIX}_visitor_id`);
    setStoredId(`${STORAGE_PREFIX}_visitor_id`, visitorId, true);

    let sessionId = getStoredId(`${STORAGE_PREFIX}_session_id`);
    setStoredId(`${STORAGE_PREFIX}_session_id`, sessionId, false);

    return {
      message,
      session_id: sessionId,
      visitor_id: visitorId,
      page_url: root.dataset.pageUrl || window.location.href,
      product_handle: root.dataset.productHandle || '',
      consent_state: getConsentState(),
      customer: getCustomerContext(root)
    };
  };

  const getMockAnswer = (message, root) => {
    const lower = String(message || '').toLowerCase();
    const loginUrl = root.dataset.loginUrl || '/account/login';
    if (lower.includes('order') || lower.includes('track')) {
      return getCustomerContext(root).authenticated
        ? 'I can help route order questions. Live Chat uses secure logged-in customer context to guide the customer without exposing sensitive order details in the page.'
        : `For order-specific help, please log into your account first so your information stays secure: ${loginUrl}. I can still answer general shipping and support questions here.`;
    }
    if (lower.includes('lab') || lower.includes('coa')) {
      return 'Live Chat can route lab-report questions to approved product knowledge, available certificates, and support next steps without making unsupported claims.';
    }
    if (lower.includes('product') || lower.includes('choose')) {
      return 'Live Chat can help compare products by format, routine, THC preference, and topical application style while keeping guidance educational and compliant.';
    }
    return 'Thanks for asking. This preview demonstrates the expandable sitewide chatbot flow. Once connected to the deployed customer-service agent endpoint, Live Chat returns approved knowledge-base answers with safe routing.';
  };

  const appendMessage = (messagesEl, role, html, isHTML = false) => {
    const row = document.createElement('div');
    row.className = `kc-floating-chat__message kc-floating-chat__message--${role}`;
    const bubble = document.createElement('div');
    bubble.className = 'kc-floating-chat__bubble';
    bubble.innerHTML = isHTML ? html : linkify(html).replace(/\n/g, '<br>');
    row.appendChild(bubble);
    messagesEl.appendChild(row);
    messagesEl.scrollTop = messagesEl.scrollHeight;
    return row;
  };

  const setLoading = (messagesEl, active, label = 'Live Chat is typing...') => {
    const existing = messagesEl.querySelector('[data-kc-chat-loading]');
    if (!active && existing) existing.remove();
    if (active && !existing) {
      const row = appendMessage(messagesEl, 'assistant', `<span class="kc-floating-chat__status"><span aria-hidden="true" class="kc-floating-chat__typing-dot"></span>${escapeHTML(label)}</span>`, true);
      row.setAttribute('data-kc-chat-loading', 'true');
    }
  };

  const setStatus = (root, text) => {
    const status = root.querySelector('[data-kc-chat-status]');
    if (status) status.textContent = text;
  };

  const submitMessage = async (root, message) => {
    const endpoint = root.dataset.apiEndpoint || '';
    const messagesEl = root.querySelector('[data-kc-chat-messages]');
    const input = root.querySelector('[data-kc-chat-input]');
    const formButton = root.querySelector('[data-kc-chat-form] button[type="submit"]');
    const cleaned = String(message || '').trim();
    if (!cleaned || !messagesEl) return;

    appendMessage(messagesEl, 'user', cleaned);
    if (input) {
      input.value = '';
      input.style.height = 'auto';
    }
    if (formButton) formButton.disabled = true;
    setStatus(root, 'Thinking');
    setLoading(messagesEl, true);

    try {
      let answer = '';
      if (!endpoint || endpoint.includes('example.com') || endpoint.includes('preview-only')) {
        await new Promise((resolve) => window.setTimeout(resolve, 450));
        answer = getMockAnswer(cleaned, root);
      } else {
        const response = await window.fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'omit',
          body: JSON.stringify(buildPayload(root, cleaned))
        });
        if (!response.ok) throw new Error(`Chat request failed with status ${response.status}`);
        const data = await response.json();
        answer = data.answer || data.message || data.response || "Sorry, I'm unable to answer that specific questions. Do you have another question?";
        if (data.login_required && data.login_url && !answer.includes(data.login_url)) {
          answer += `\n\nLogin for order-specific help: ${data.login_url}`;
        }
        if (data.session_id) setStoredId(`${STORAGE_PREFIX}_session_id`, data.session_id, false);
      }
      setLoading(messagesEl, false);
      appendMessage(messagesEl, 'assistant', answer);
      setStatus(root, 'Ready');
    } catch (error) {
      setLoading(messagesEl, false);
      appendMessage(messagesEl, 'assistant', "Sorry, I'm unable to answer that specific questions. Do you have another question?");
      setStatus(root, 'Connection issue');
      if (window.console && typeof window.console.warn === 'function') {
        window.console.warn('[Live Chat] Request failed:', error);
      }
    } finally {
      if (formButton) formButton.disabled = false;
      if (input && !root.matches('[data-kc-chat-closed="true"]')) input.focus();
    }
  };

  const openWidget = (root) => {
    const launcher = root.querySelector('[data-kc-chat-launcher]');
    const panel = root.querySelector('[data-kc-chat-panel]');
    const input = root.querySelector('[data-kc-chat-input]');
    if (!panel || !launcher) return;
    root.dataset.kcChatOpen = 'true';
    setSessionFlag(DISMISSED_KEY, false);
    panel.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    root.dispatchEvent(new CustomEvent('agentaskme:open', { bubbles: true }));
    window.setTimeout(() => input && input.focus(), 50);
  };

  const closeWidget = (root, focusLauncher = true, rememberDismissal = true) => {
    const launcher = root.querySelector('[data-kc-chat-launcher]');
    const panel = root.querySelector('[data-kc-chat-panel]');
    if (!panel || !launcher) return;
    root.dataset.kcChatOpen = 'false';
    if (rememberDismissal) setSessionFlag(DISMISSED_KEY, true);
    panel.hidden = true;
    launcher.setAttribute('aria-expanded', 'false');
    root.dispatchEvent(new CustomEvent('agentaskme:close', { bubbles: true }));
    if (focusLauncher) launcher.focus();
  };

  const initWidget = (root) => {
    if (root.dataset.kcChatInitialized === 'true') return;
    root.dataset.kcChatInitialized = 'true';

    const launcher = root.querySelector('[data-kc-chat-launcher]');
    const closeButton = root.querySelector('[data-kc-chat-close]');
    const minimizeButton = root.querySelector('[data-kc-chat-minimize]');
    const form = root.querySelector('[data-kc-chat-form]');
    const input = root.querySelector('[data-kc-chat-input]');
    const prompts = root.querySelectorAll('[data-kc-chat-prompt]');

    if (launcher) launcher.addEventListener('click', () => openWidget(root));
    if (closeButton) closeButton.addEventListener('click', () => closeWidget(root));
    if (minimizeButton) minimizeButton.addEventListener('click', () => closeWidget(root));

    if (form) {
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        submitMessage(root, input ? input.value : '');
      });
    }

    if (input) {
      input.addEventListener('input', () => {
        input.style.height = 'auto';
        input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          event.preventDefault();
          submitMessage(root, input.value);
        }
      });
    }

    prompts.forEach((button) => {
      button.addEventListener('click', () => {
        const prompt = button.getAttribute('data-kc-chat-prompt') || button.textContent;
        openWidget(root);
        submitMessage(root, prompt);
      });
    });

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-kc-floating-chat-open], [data-agent-askme-open]');
      if (opener) {
        event.preventDefault();
        openWidget(root);
      }
    });

    root.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeWidget(root);
    });

    if (root.dataset.autoOpen === 'true') {
      const delay = Math.max(0, Number(root.dataset.autoOpenDelay || 0));
      window.setTimeout(() => {
        if (!isSmallScreen() && !getSessionFlag(DISMISSED_KEY) && root.dataset.kcChatOpen !== 'true') openWidget(root);
      }, delay);
    }
  };

  const initAll = () => document.querySelectorAll(WIDGET_SELECTOR).forEach(initWidget);

  window.AgentAskMeWidget = window.AgentAskMeWidget || {
    open: () => {
      const root = document.querySelector(WIDGET_SELECTOR);
      if (root) openWidget(root);
    },
    close: () => {
      const root = document.querySelector(WIDGET_SELECTOR);
      if (root) closeWidget(root, false, true);
    },
    init: initAll
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
